import type { TemplateSection } from "./template-contract"
import type { ContentNode } from "./rich-text"

export type CanvasField = { field: string; section?: string; item?: number; part?: "title" | "copy" | "line" }
export type CanvasDocument = { values: Record<string, unknown>; sections: TemplateSection[] }

export function canvasFieldKey(target: CanvasField) {
  return JSON.stringify([target.section || "page", target.field, target.item ?? null, target.part || null])
}
export function canvasFieldFormats(sections: TemplateSection[]) {
  return Object.assign(Object.create(null), ...sections.map(section => section.fieldFormats || {})) as Record<string, ContentNode[]>
}
export function canvasFormattedValue(state: CanvasDocument, target: CanvasField) {
  if (target.section && target.field === "body" && target.item === undefined) return canvasSection(state.sections, target.section)?.richBody
  const formatted = canvasFieldFormats(state.sections)[canvasFieldKey(target)]
  const text = (node: ContentNode | { text: string }): string => "text" in node ? String(node.text) : node.children.map(child => text(child as ContentNode | { text: string })).join("")
  return formatted?.map(text).join(" ") === canvasFieldValue(state, target) ? formatted : undefined
}
export function canvasFieldSections(state: CanvasDocument, target: CanvasField, text: string, richBody?: ContentNode[]) {
  const value = canvasFieldPatch(state, target, text)
  const formats = canvasFieldFormats(state.sections)
  if (richBody && !(target.section && target.field === "body" && target.item === undefined)) formats[canvasFieldKey(target)] = richBody
  else delete formats[canvasFieldKey(target)]
  return state.sections.map((section, index) => ({ ...section,
    ...(target.section && (section.templateKey === target.section || section.id === target.section) ? { [target.field]: value, ...(target.field === "body" && target.item === undefined ? { richBody } : {}) } : {}),
    fieldFormats: index === 0 ? formats : undefined,
  }))
}

export function canvasSection(sections: TemplateSection[], key: string) {
  return sections.find(section => section.templateKey === key || section.id === key)
}
export function canvasFieldValue(state: CanvasDocument, target: CanvasField) {
  const section = target.section ? canvasSection(state.sections, target.section) : undefined
  const value = String((target.section ? section?.[target.field as keyof TemplateSection] : state.values[target.field]) ?? "")
  if (target.item === undefined) return value
  const line = value.split("\n").filter(Boolean)[target.item] || ""
  if (target.part === "line") return line.replace(/^[-*]\s*/, "")
  const step = /^\*\*(.*?)\*\*\s*[—–-]\s*([\s\S]*)$/.exec(line.replace(/^\d+\.\s*/, ""))
  return target.part === "title" ? step?.[1] || line : step?.[2] || ""
}
export function canvasFieldPatch(state: CanvasDocument, target: CanvasField, text: string) {
  if (target.item === undefined) return text
  const whole = canvasFieldValue(state, { ...target, item: undefined })
  const lines = whole.split("\n").filter(Boolean)
  const previous = lines[target.item] || ""
  if (target.part === "line") lines[target.item] = /^[-*]\s/.test(previous) ? `- ${text}` : text
  else {
    const title = target.part === "title" ? text : canvasFieldValue(state, { ...target, part: "title" })
    const copy = target.part === "copy" ? text : canvasFieldValue(state, { ...target, part: "copy" })
    lines[target.item] = `${previous.match(/^\d+\.\s*/)?.[0] || ""}**${title}** — ${copy}`
  }
  return lines.join(target.part === "line" ? "\n" : "\n\n")
}
